import { BUILDINGS } from '../../shared/constants'
import { TOWN_HALL_PHASES, townHallPhase } from '../../shared/townhall'
import { RESOURCES } from '../../shared/types'
import { pickEntity } from '../pick'
import { use3dView } from '../state'
import { storageStatus } from '../storage'
import { el, RES_ICON, type UICtx } from './panels'

/** Quick, live inventory over a hub; never intercepts selection or commands. */
export function initStorageHover({ st, root }: UICtx): void {
  const canvas = document.getElementById('game') as HTMLCanvasElement | null
  if (!canvas) return
  const use3d = use3dView()
  const tip = el('div', 'restip storage-tip hidden')
  tip.setAttribute('role', 'tooltip')
  tip.setAttribute('aria-label', 'Storage contents')
  root.appendChild(tip)
  let pointer: { x: number; y: number } | null = null
  let signature = ''
  let dragged = false

  const hide = (): void => { tip.classList.add('hidden') }
  const clear = (): void => { pointer = null; hide() }
  const trackPointer = (e: PointerEvent): void => {
    if (e.buttons) dragged = true
    if (e.target !== canvas || e.pointerType === 'touch' || e.buttons) { clear(); return }
    pointer = { x: e.clientX, y: e.clientY }
  }
  window.addEventListener('pointermove', trackPointer)
  // A drag moves the camera after the input layer's last pick. Wait for a
  // fresh move before showing the card again; an ordinary click keeps it.
  window.addEventListener('pointerup', e => { if (!dragged) trackPointer(e) })
  canvas.addEventListener('pointerleave', clear)
  window.addEventListener('pointerdown', () => { dragged = false; clear() })
  window.addEventListener('pointercancel', clear)
  window.addEventListener('blur', clear)
  window.addEventListener('wheel', clear, { passive: true })
  window.addEventListener('keydown', clear)
  document.addEventListener('visibilitychange', clear)

  window.setInterval(() => {
    // Hover picks can outlive the cursor leaving the scene. Check the actual
    // surface too, including menus that open beneath a stationary pointer.
    if (!pointer || !st.connected || st.buildMode || document.hidden
      || document.elementFromPoint(pointer.x, pointer.y) !== canvas) { hide(); return }
    const rect = canvas.getBoundingClientRect()
    const hovered = use3d ? st.hoverEnt : pickEntity(st, canvas, pointer.x - rect.left, pointer.y - rect.top)
    const b = hovered?.kind === 'building' ? st.buildings.get(hovered.id) : undefined
    // Other realms' store records are deliberately stripped on the wire.
    if (!b || b.owner !== st.me || !b.done || (b.type !== 'townhall' && b.type !== 'storehouse')) { hide(); return }
    const { used: total, capacity: cap, remaining, level, full } = storageStatus(b)
    const amounts = RESOURCES.map(res => Math.floor(b.store?.[res] ?? 0))
    const name = b.name || (b.type === 'townhall'
      ? TOWN_HALL_PHASES[townHallPhase(b.hallPhase) - 1].name : BUILDINGS[b.type].name)
    const next = JSON.stringify([b.id, name, cap, total, amounts])
    if (next !== signature) {
      signature = next
      tip.replaceChildren()
      const head = el('div', 'rt-head')
      head.append(el('span', 'rt-ico', '📦'), el('span', 'rt-name', name + ' storage'))
      tip.appendChild(head)
      const space = el('div', 'storage-space')
      space.append(el('strong', '', `${Math.floor(total)} / ${cap}`), el('span', '', 'items stored'))
      tip.appendChild(space)
      const meter = el('div', 'storage-meter')
      const fill = el('div', 'storage-meter-fill')
      fill.style.width = `${Math.min(100, total / Math.max(1, cap) * 100)}%`
      meter.appendChild(fill)
      tip.appendChild(meter)
      tip.classList.toggle('near-full', level === 'near')
      tip.classList.toggle('critical', level === 'critical')
      tip.appendChild(el('div', 'storage-free', full ? 'Storage full'
        : `${Math.floor(remaining)} space remaining${level !== 'normal' ? ' · Nearly full' : ''}`))
      const goods = el('div', 'storage-goods')
      RESOURCES.forEach((res, i) => {
        const row = el('div', 'storage-good' + (amounts[i] === 0 ? ' empty' : ''))
        row.append(el('span', '', `${RES_ICON[res]} ${res[0].toUpperCase() + res.slice(1)}`), el('strong', '', String(amounts[i])))
        goods.appendChild(row)
      })
      tip.appendChild(goods)
      tip.appendChild(el('div', 'storage-hint', 'Click to manage storage'))
    }
    tip.classList.remove('hidden')
    // Sit beside the cursor so the hovered model stays visible. Flip at the
    // right/bottom edges and clamp to keep every material on screen.
    const gap = 18, margin = 10
    const width = tip.offsetWidth, height = tip.offsetHeight
    let left = pointer.x + gap, top = pointer.y - height / 2
    if (left + width > window.innerWidth - margin) left = pointer.x - width - gap
    left = Math.max(margin, Math.min(left, window.innerWidth - width - margin))
    top = Math.max(margin, Math.min(top, window.innerHeight - height - margin))
    tip.style.left = Math.round(left) + 'px'
    tip.style.top = Math.round(top) + 'px'
  }, 100)
}
